import {Component, HostListener, OnInit} from '@angular/core';
import {MatCard, MatCardContent} from "@angular/material/card";
import {ActivityFactory} from "../activity/activity-factory.service";
import {DatePipe, DecimalPipe, NgClass, NgIf} from "@angular/common";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {PaginatorModule} from "primeng/paginator";
import {PrimeTemplate} from "primeng/api";
import {TableModule} from "primeng/table";
import {MatButton} from "@angular/material/button";
import {
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepickerActions,
  MatDatepickerApply,
  MatDatepickerToggle,
  MatDateRangeInput,
  MatDateRangePicker,
  MatEndDate,
  MatStartDate
} from "@angular/material/datepicker";
import {MatFormField, MatLabel, MatSuffix} from "@angular/material/form-field";
import {FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {UserService} from "../service/user.service";
import {Observable, take} from "rxjs";
import {MatInput} from "@angular/material/input";
import {InsightsService} from "../service/insights.service";
import {CardViewsStat} from "../models/cardViewsStat";
import {DownloadedStat} from "../models/downloadedStat";
import {ContentStat} from "../models/ContentStat";
import {TopContentStat} from "../models/TopContentStat";
import {ContentService} from "../service/content.service";
import {ThirtyDayRangeSelectionStrategy} from "../utilityClass/ThirtyDayRangeSelectionStrategy";
import {DateAdapter} from "@angular/material/core";
import {CanvasJSAngularChartsModule} from "@canvasjs/angular-charts";
import {Chart, registerables} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {TopPerformers} from "../models/TopPerformers";
import {TeamService} from "../service/team.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";

Chart.register(...registerables, ChartDataLabels);

@Component({
  selector: 'app-my-insights',
  templateUrl: './my-insights.component.html',
  standalone: true,
  imports: [
    MatCard,
    MatCardContent,
    DatePipe,
    MatIcon,
    MatTooltip,
    NgIf,
    PaginatorModule,
    PrimeTemplate,
    TableModule,
    MatButton,
    MatDateRangeInput,
    MatDateRangePicker,
    MatDatepickerActions,
    MatDatepickerApply,
    MatDatepickerToggle,
    MatEndDate,
    MatFormField,
    MatLabel,
    MatStartDate,
    MatSuffix,
    ReactiveFormsModule,
    MatInput,
    CanvasJSAngularChartsModule,
    NgClass,
    DecimalPipe,
    TranslateModule
  ],
  providers: [
    DatePipe,
    {provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useClass: ThirtyDayRangeSelectionStrategy}
  ],
  styleUrl: './my-insights.component.scss'
})
export class MyInsightsComponent implements OnInit {
  name!: string;
  surname!: string;
  dateRange: FormGroup;
  displayDateRange: string | null = '';
  cardViewStat!: CardViewsStat;
  downloadedStat!: DownloadedStat;
  contentStat!: ContentStat;
  linkTapData!: TopContentStat[];
  today = new Date();
  isMobile!: boolean;
  isIndividualActive: boolean = true;
  isTeam: boolean = false;
  topUsers: TopPerformers[] = [];
  private chartInstance: Chart | null = null;
  isCurrentUserOwnerOfTeam!:boolean;

  constructor(private fb: FormBuilder, private datePipe: DatePipe,
              private userService: UserService,
              private insightsService: InsightsService,
              private contentService: ContentService,
              private teamService : TeamService,
              private dateAdapter: DateAdapter<Date>,
              private translate: TranslateService) {
    this.dateRange = this.fb.group({
      start: null,
      end: null
    });
    this.today = this.dateAdapter.today();
    this.isMobile = window.innerWidth < 769;
    this.teamService.isCurrentUserOwnerTeam().subscribe({
      next: (value) => {
        this.isCurrentUserOwnerOfTeam = value;
      }
    });
  }

  ngOnInit() {
    this.initializeCurrentUser();
    this.initializeDateRange();
    this.fetchData();
    this.fetchTopPerformers();
  }

  fetchTopPerformers(startDate?: string | null, endDate?: string | null) {
    this.insightsService.getTopPerformers(startDate, endDate).subscribe({
      next: (data) => {
        this.topUsers = data;
        this.createTopPerformersChart();
      },
      error: (err) => {
        console.error('Error fetching top performers:', err);
      },
    });
  }

  createTopPerformersChart() {
    const ctx = document.getElementById('topPerformersChart') as HTMLCanvasElement;
    this.destroyExistingChart();
    const truncatedNames = this.getTruncatedNames();
    const images = this.getUserImages();
    this.loadImages(images).then(() => {
      this.initializeChart(ctx, truncatedNames, images);
    });
  }

   initializeChart(ctx: HTMLCanvasElement, truncatedNames: string[], images: HTMLImageElement[]) {
      const indexAxis = window.innerWidth >= 768 ? 'x' : 'y';
      this.chartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: truncatedNames,
          datasets: [
            {
              label: this.translate.instant('insights.card_views'),
              data: this.topUsers.map((user) => user.cardViews),
              backgroundColor: 'rgba(0, 54, 179, 1)',
              borderRadius: 6,
              barThickness: 30
            },
          ],
        },
        options: {
          indexAxis,
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            datalabels: {
              display: false,
            },
          },
          scales: {
            x: {
              beginAtZero: true,

              grid: {
                display: false,
                drawBorder: false,
              },
              ticks: {
                display: indexAxis === 'x',
                font: {
                  size: 14,
                },
                textStrokeWidth: 50,
                color: 'rgba(0, 0, 0, 0.6)',
                minRotation: 0,
                maxRotation: 0,
                align: 'center',
                padding: 35,
              },
            },
            y: {
              beginAtZero: true,
              grid: {
                display: false,
                drawBorder: false,
              },
              ticks: {
                stepSize: 100,
                display: indexAxis === 'y',
                font: {
                  size: 14,
                },
                textStrokeWidth: 50,
                color: 'rgba(0, 0, 0, 0.6)',
                minRotation: 0,
                maxRotation: 0,
                align: 'center',
                padding: 40,
              },
            },
          },
        },
        plugins: [
          {
            id: 'customUserImagesAndText',
            afterDatasetsDraw: (chart) => {
              const ctx = chart.ctx;
              this.topUsers.forEach((user, index) => {
                const meta = chart.getDatasetMeta(0);
                const bar = meta.data[index];
                const image = images[index];
                const x = indexAxis === 'x' ? bar.x : chart.chartArea.top + 55;
                const y = indexAxis === 'x' ? chart.chartArea.bottom + 20  : bar.y;
                ctx.save();
                ctx.beginPath();
                ctx.arc(x, y, 15, 0, Math.PI * 2);
                ctx.closePath();
                ctx.clip();

                if (image.complete && image.naturalWidth > 0) {
                  ctx.drawImage(image, x - 15, y - 15, 30, 30);
                } else {
                  console.error('Image not ready:', image.src);
                }
                ctx.restore();
                ctx.fillStyle = 'white';
                ctx.textAlign = 'center';
                ctx.font = '14px Sans-Serif, sans-serif';
                const labelX = indexAxis === 'x' ? bar.x : bar.x - 20;
                const labelY = indexAxis === 'x' ? bar.y + 15 : bar.y + 5;
                ctx.fillText(user.cardViews.toString(), labelX, labelY);
              });
            },
          },
        ],
      });
    }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.isMobile = window.innerWidth < 769;
  }

  fetchData(startDate?: string | null, endDate?: string | null): void {
    const isTeam = !this.isIndividualActive;
    this.fetchStat(this.insightsService.getCardViewStat(startDate, endDate, isTeam), 'cardViewStat');
    this.fetchStat(this.insightsService.getDownloadedStat(startDate, endDate, isTeam), 'downloadedStat');
    this.fetchStat(this.insightsService.getContentStat(startDate, endDate, isTeam), 'contentStat');
    this.fetchStat(this.insightsService.getTopContentStat(startDate, endDate, isTeam), 'linkTapData');
  }

  fetchStat<T>(serviceCall: Observable<T>, propertyName: keyof this): void {
    serviceCall.subscribe({
      next: (response: T) => {
        this[propertyName] = response as any;
      },
      error: (err) => console.error(`Failed to fetch ${String(propertyName)}`, err),
    });
  }

  toggleIndividual(value: boolean) {
    this.isIndividualActive = value;
    ActivityFactory.isIndividualActiveInSight.next(value)
    this.fetchData(
      this.datePipe.transform(this.dateRange.get('start')?.value, "yyyy-MM-dd'T'HH:mm:ss"),
      this.datePipe.transform(this.dateRange.get('end')?.value, "yyyy-MM-dd'T'HH:mm:ss")
    );
  }

  getContentIcon(image: string) {
    return this.contentService.getImage(image)
  }

  onDateRangeChange() {
    const startDate = this.datePipe.transform(this.dateRange.get('start')?.value, "yyyy-MM-dd'T'HH:mm:ss");
    const endDate = this.datePipe.transform(this.dateRange.get('end')?.value, "yyyy-MM-dd'T'HH:mm:ss");

    this.fetchData(startDate, endDate);
    this.fetchTopPerformers(startDate, endDate);
  }

  resetDateRange(picker: MatDateRangePicker<any>): void {
    this.dateRange.reset();
    this.displayDateRange = '';
    this.fetchData();
    this.fetchTopPerformers();
    picker.close();
  }

  private initializeCurrentUser(): void {
    this.userService.currentUser.pipe(take(1)).subscribe({
      next: user => {
        this.name = user.name;
        this.surname = user.surname;
      },
      error: error => alert('Error: ' + error.message)
    });
  }

  private initializeDateRange(): void {
    this.dateRange.valueChanges.subscribe(({start, end}) => {
      if (start && end) {
        const formattedStart = this.datePipe.transform(start, 'MMM d');
        const formattedEnd = this.datePipe.transform(end, 'MMM d');
        this.displayDateRange = `${formattedStart} - ${formattedEnd}`;
      } else {
        this.displayDateRange = '';
      }
    });
  }

  private destroyExistingChart() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  }

  private getTruncatedNames(): string[] {
    return this.topUsers.map((user) =>
      user.cardName.length > 6 ? `${user.cardName.slice(0, 6)}...` : user.cardName
    );
  }

  private getUserImages(): HTMLImageElement[] {
    return this.topUsers.map((user) => {
      const image = new Image();
      image.src = user.cardImage
        ? this.userService.getImage(user.cardImage)
        : 'assets/svg/defaultUserIcon.svg';
      return image;
    });
  }
  private loadImages(images: HTMLImageElement[]): Promise<void[]> {
    return Promise.all(
      images.map((img) =>
        new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
      )
    );
  }
}
